import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  Copy,
  Check,
  Eye
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import RefreshButton from '../../components/ui/RefreshButton';
import { userAPI } from '../../services/api';
import Button from '../../components/ui/Button';
import Card, { CardContent } from '../../components/ui/Card';
import CarLoader from '../../components/ui/CarLoader';
import Toast from '../../components/ui/Toast';
import { formatName, timeAgo, formatDateTime } from '../../lib/utils';

const roleBadgeColors = {
  manager: 'bg-purple-100 text-purple-800',
  accountant: 'bg-green-100 text-green-800',
  dispatcher: 'bg-blue-100 text-blue-800',
  driver: 'bg-orange-100 text-orange-800',
  staff: 'bg-gray-100 text-gray-800'
};

const roleIcons = {
  manager: '👔',
  accountant: '📊',
  dispatcher: '📍',
  driver: '🚗',
  staff: '👤'
};

const StaffList = () => {
  const navigate = useNavigate();
  const [allStaffMembers, setAllStaffMembers] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [groupedByRole, setGroupedByRole] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, staffId: null, staffName: '' });

  useEffect(() => {
    fetchStaffMembers();
  }, []);

  // Filter locally when role changes
  useEffect(() => {
    if (filterRole) {
      setStaffMembers(allStaffMembers.filter(s => s.role === filterRole));
    } else {
      setStaffMembers(allStaffMembers);
    }
  }, [filterRole, allStaffMembers]);

  const fetchStaffMembers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getStaffMembers({});
      const members = response.data.staffMembers || [];
      setAllStaffMembers(members);
      setStaffMembers(members);
      setGroupedByRole(response.data.groupedByRole || {});
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    const { staffId } = confirmModal;
    setConfirmModal({ isOpen: false, staffId: null, staffName: '' });
    try {
      await userAPI.deleteStaff(staffId);
      fetchStaffMembers();
      setToast({
        isOpen: true,
        title: 'Staff Deactivated',
        message: 'The staff member has been deactivated successfully.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting staff:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: 'Failed to deactivate staff member. Please try again.',
        type: 'error'
      });
    }
  };

  const copyCredentials = (staff) => {
    const text = `Login: ${staff.phone}\nURL: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text);
    setCopiedId(staff._id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredStaff = staffMembers.filter(staff => {
    const matchesSearch = staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (staff.email && staff.email.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const totalByRole = {
    manager: groupedByRole.manager?.length || 0,
    accountant: groupedByRole.accountant?.length || 0,
    dispatcher: groupedByRole.dispatcher?.length || 0,
    driver: groupedByRole.driver?.length || 0,
    staff: groupedByRole.staff?.length || 0
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team Members"
        description="Manage your staff and drivers"
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={fetchStaffMembers} loading={loading} />
            <Button variant="primary" onClick={() => navigate('/staff/new')}>
              <Plus className="h-5 w-5 mr-2" />
              Add Member
            </Button>
          </div>
        }
      />

      {/* Stats Cards — mobile: horizontal-scroll strip of small cards.
          Desktop (sm+): compact 5-column grid; icon and count sit on the
          same row so the cards stay short vertically — the user list
          below is the primary content. */}
      <div className="flex sm:grid sm:grid-cols-5 gap-2 sm:gap-3 mb-3 overflow-x-auto sm:overflow-visible pb-1 sm:pb-0 -mx-1 sm:mx-0 px-1 sm:px-0">
        {Object.entries(totalByRole).map(([role, count]) => (
          <div
            key={role}
            className={`cursor-pointer p-2 sm:px-3 sm:py-2 rounded-lg border transition-all text-center sm:text-left min-w-[72px] sm:min-w-0 flex-shrink-0 sm:flex-shrink ${
              filterRole === role
                ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200'
                : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            }`}
            onClick={() => setFilterRole(filterRole === role ? '' : role)}
          >
            {/* Mobile: icon on top, count, then label (vertical stack) */}
            {/* Desktop: icon + count in one row, label below (horizontal compact) */}
            <span className="text-lg block leading-none sm:hidden">{roleIcons[role]}</span>
            <p className="text-lg font-bold text-gray-900 sm:hidden">{count}</p>
            <p className="text-[10px] text-gray-500 capitalize truncate sm:hidden">{role}s</p>

            <div className="hidden sm:flex items-center gap-2">
              <span className="text-xl leading-none">{roleIcons[role]}</span>
              <p className="text-xl font-bold text-gray-900 leading-none">{count}</p>
            </div>
            <p className="hidden sm:block text-xs text-gray-500 capitalize truncate mt-0.5">{role}s</p>
          </div>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Roles</option>
          <option value="manager">Managers</option>
          <option value="accountant">Accountants</option>
          <option value="dispatcher">Dispatchers</option>
          <option value="driver">Drivers</option>
          <option value="staff">Staff</option>
        </select>
      </div>

      {/* Applied Filters */}
      {(filterRole || searchTerm) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">Filters:</span>
          {filterRole && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
              {roleIcons[filterRole]} {filterRole}s
              <button
                onClick={() => setFilterRole('')}
                className="ml-0.5 hover:text-blue-900"
              >
                ×
              </button>
            </span>
          )}
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full border border-gray-200">
              Search: "{searchTerm}"
              <button
                onClick={() => setSearchTerm('')}
                className="ml-0.5 hover:text-gray-900"
              >
                ×
              </button>
            </span>
          )}
          <button
            onClick={() => { setFilterRole(''); setSearchTerm(''); }}
            className="text-xs text-red-500 hover:text-red-700 font-medium ml-1"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Staff List */}
      {loading ? (
        <div className="py-6">
          <CarLoader message="Loading team members..." />
        </div>
      ) : filteredStaff.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">No team members yet</h3>
            <p className="text-xs text-gray-600 mb-3">Start by adding your first team member</p>
            <Button size="sm" onClick={() => navigate('/staff/new')}>
              <Plus className="h-4 w-4 mr-1" />
              Add Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredStaff.map((staff) => (
            <Card
              key={staff._id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${!staff.isActive ? 'opacity-60' : ''}`}
              onClick={() => navigate(`/staff/${staff._id}`)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-sm overflow-hidden flex-shrink-0 ${
                      staff.isActive ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gray-400'
                    }`}>
                      {staff.photo?.url ? (
                        <img src={staff.photo.url} alt={staff.name} className="w-full h-full object-cover" />
                      ) : (
                        formatName(staff.name).charAt(0)
                      )}
                    </div>

                    {/* Info */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm">{formatName(staff.name)}</h3>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${roleBadgeColors[staff.role]}`}>
                          {roleIcons[staff.role]} {staff.role}
                        </span>
                        {!staff.isActive && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-800">Inactive</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
                        {staff.phone && (
                          <span className="flex items-center text-blue-600 font-medium">
                            <Phone className="h-3 w-3 mr-1" />
                            {staff.phone}
                          </span>
                        )}
                        {staff.email && (
                          <span className="flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {staff.email}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5" title={staff.lastActiveAt ? formatDateTime(staff.lastActiveAt) : 'Never used'}>
                        Last used: {staff.lastActiveAt ? timeAgo(staff.lastActiveAt) : 'Never'}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/staff/${staff._id}`)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/staff/${staff._id}/edit`)}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => copyCredentials(staff)}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors hidden md:block"
                      title="Copy credentials"
                    >
                      {copiedId === staff._id ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setConfirmModal({ isOpen: true, staffId: staff._id, staffName: staff.name })}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Deactivate"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirm Deactivate Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-rose-600 px-6 py-5 text-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                <Trash2 className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white">Deactivate Member</h3>
            </div>
            <div className="p-5 text-center">
              <p className="text-gray-600 text-sm mb-5">
                Are you sure you want to deactivate <span className="font-semibold text-gray-900">{formatName(confirmModal.staffName)}</span>? They will no longer be able to access the system.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ isOpen: false, staffId: null, staffName: '' })}
                  className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivateConfirm}
                  className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default StaffList;
